// src/presentation/pages/ClinicalExamination/Step4CognitiveFunctional.tsx
import React from "react";

import type { CognitiveFunctionalFormData } from "./types";
import {
  ExecutiveFunctionSection,
  LanguageSection,
  MemorySection,
  MotorSymptomsSection,
  PerceptionSleepSection,
  VisuospatialSection,
} from "./sections";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

interface Step4CognitiveFunctionalProps {
  formData: CognitiveFunctionalFormData;
  onFormDataChange: (data: CognitiveFunctionalFormData) => void;
  onSkip?: () => void;
}

const Step4CognitiveFunctional: React.FC<Step4CognitiveFunctionalProps> = ({
  formData,
  onFormDataChange,
  onSkip,
}) => {
  const { t } = useTranslation("patients");
  const updateFormData = (updates: Partial<CognitiveFunctionalFormData>) => {
    onFormDataChange({ ...formData, ...updates });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col">
        <p className="text-[#5F646F] text-sm"> {t("clinicalSections.step4.stepLabel")}</p>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <h3 className="text-lg font-bold text-gray-900">
              {t("clinicalSections.step4.title")}
            </h3>
            <div className="bg-[#D4DAEB] p-2 rounded">
              <p className="">{t("clinicalSections.step4.answeredByCaregiver")}</p>
            </div>
          </div>
          <Button
            variant="link"
            onClick={onSkip}
            className="hover:text-primary cursor-pointer"
          >
            {t("clinicalSections.step4.skip")}
          </Button>
        </div>
      </div>

      {/* Memory Section */}
      <MemorySection
        forgetsRecentEvents={formData.forgetsRecentEvents}
        repeatsQuestions={formData.repeatsQuestions}
        onForgetsRecentEventsChange={(value) =>
          updateFormData({ forgetsRecentEvents: value })
        }
        onRepeatsQuestionsChange={(value) =>
          updateFormData({ repeatsQuestions: value })
        }
      />
      <Separator />

      {/* Executive Function Section */}
      <ExecutiveFunctionSection
        difficultyPlanning={formData.difficultyPlanning}
        difficultyManagingFinances={formData.difficultyManagingFinances}
        onDifficultyPlanningChange={(value) =>
          updateFormData({ difficultyPlanning: value })
        }
        onDifficultyManagingFinancesChange={(value) =>
          updateFormData({ difficultyManagingFinances: value })
        }
      />
      <Separator />

      {/* Language Section */}
      <LanguageSection
        difficultyFindingWords={formData.difficultyFindingWords}
        difficultyUnderstandingSpeech={formData.difficultyUnderstandingSpeech}
        onDifficultyFindingWordsChange={(value) =>
          updateFormData({ difficultyFindingWords: value })
        }
        onDifficultyUnderstandingSpeechChange={(value) =>
          updateFormData({ difficultyUnderstandingSpeech: value })
        }
      />
      <Separator />

      {/* Visuospatial Section */}
      <VisuospatialSection
        getsLostFamiliarPlaces={formData.getsLostFamiliarPlaces}
        difficultyRecognizingFaces={formData.difficultyRecognizingFaces}
        onGetsLostFamiliarPlacesChange={(value) =>
          updateFormData({ getsLostFamiliarPlaces: value })
        }
        onDifficultyRecognizingFacesChange={(value) =>
          updateFormData({ difficultyRecognizingFaces: value })
        }
      />
      <Separator />

      {/* Perception & Sleep Behavior Section */}
      <PerceptionSleepSection
        visualHallucinations={formData.visualHallucinations}
        actingOutDreams={formData.actingOutDreams}
        onVisualHallucinationsChange={(value) =>
          updateFormData({ visualHallucinations: value })
        }
        onActingOutDreamsChange={(value) =>
          updateFormData({ actingOutDreams: value })
        }
      />
      <Separator />
      {/* Motor Symptoms Section */}
      <MotorSymptomsSection
        slownessOrStiffness={formData.slownessOrStiffness}
        slowMovementOrBalanceIssues={formData.slowMovementOrBalanceIssues}
        onSlownessOrStiffnessChange={(value) =>
          updateFormData({ slownessOrStiffness: value })
        }
        onSlowMovementOrBalanceIssuesChange={(value) =>
          updateFormData({ slowMovementOrBalanceIssues: value })
        }
      />
    </div>
  );
};

export default Step4CognitiveFunctional;
